import {
  CSSProperties,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SIZES } from '@ezugi/constants';

import { DeviceInfo } from '../../../utils/src';
import { Container, hiddenChipTextClassName } from './style';

const DEFAULT_CHIP_SIZE = SIZES.CHIP_SIZE || 40;
const DEFAULT_OFFSET = 2;
const BASE_FONT_SIZE = 4;
const CHIP_OFFSET = 0.25;
const CONTAINER_SIZE = 10; // chip viewBox

/**
 * Workaround for text rendering issues with fullscreen interaction
 */
const { isIOS, isAndroid, isSafari, isChrome, isFirefox } = DeviceInfo;

const textRendering = isSafari || isIOS || isChrome ? 'auto' : 'geometricPrecision';

const textBaseline = isIOS || isAndroid ? 'middle' : 'central';

const dropShadowFilter = isFirefox ? 'drop-shadow(0px 7px 10px rgba(0,0,0,0.7))' : 'url(#drop-shadow)';

const PLATFORM_TEXT_TRANSFORM = {
  android: 'translate(0, 0.3)',
  ios: 'translate(0.1, 0.1)',
  desktop: undefined,
};

const textTransform = isIOS ? PLATFORM_TEXT_TRANSFORM.ios
  : isAndroid ? PLATFORM_TEXT_TRANSFORM.android : PLATFORM_TEXT_TRANSFORM.desktop;

type ChipProps = {
  className?: string;
  showValue?: boolean;
  onClick?: () => void;
  offset?: number;
  selected?: boolean;
  disabled?: boolean;
  size?: number;
  img: string;
  color?: string;
  style?: CSSProperties;
  perspective?: boolean;
  dataE2e?: string;
  value: number | string;
  displayValue?: number | string;
  hasDropShadow?: boolean;
};

const Chip = ({
  size = DEFAULT_CHIP_SIZE,
  value,
  displayValue,
  selected = false,
  showValue = true,
  disabled = false,
  onClick = () => {},
  img,
  offset = DEFAULT_OFFSET,
  perspective = false,
  dataE2e = 'chip-value',
  hasDropShadow,
  ...rest
}: ChipProps) => {
  const [ fontSize, setFontSize ] = useState(BASE_FONT_SIZE);
  const textNode = useRef<SVGTextElement>(null);
  const maxSize = CONTAINER_SIZE - offset * 2;

  useLayoutEffect(() => {
    if (!textNode.current) return;
    const textLength = textNode.current.getComputedTextLength();
    const newFontSize = textLength > maxSize
      ? parseFloat(((BASE_FONT_SIZE / textLength) * maxSize).toFixed(2)) : BASE_FONT_SIZE;
    setFontSize(newFontSize);
  }, [ value, displayValue, maxSize ]);

  const handleOnClick = useCallback(() => {
    if (!disabled) {
      onClick();
    }
  }, [ disabled, onClick ]);

  const imageHeight = useMemo(
    () => (perspective
      ? `calc(100% - ${CHIP_OFFSET}px)`
      : '100%'),
    [ perspective ]
  );

  return (
    <Container
      data-e2e={`chip-${value}`}
      {...rest}
      width={size}
      height={size}
      style={{
        ...(selected && { filter: 'drop-shadow(0 0 10px #cb9434)' }),
        ...(hasDropShadow && { filter: dropShadowFilter }),
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'auto' : 'pointer',
        ...rest.style,
      }}
      viewBox="0 0 10 10"
      onClick={handleOnClick}
      data-selected={selected}
      data-value={value}
      data-disabled={disabled}
    >
      {perspective && (
        <>
          <image
            y={CHIP_OFFSET * 3}
            height={imageHeight}
            width="100%"
            xlinkHref={img}
            href={img}
          />
          <image
            y={CHIP_OFFSET * 2}
            height={imageHeight}
            width="100%"
            xlinkHref={img}
            href={img}
          />
          <image
            y={CHIP_OFFSET}
            height={imageHeight}
            width="100%"
            xlinkHref={img}
            href={img}
          />
        </>
      )}
      <image
        height={imageHeight}
        width="100%"
        xlinkHref={img}
        href={img}
      />
      {showValue && (
        <>
          <text
            ref={textNode}
            fontSize={BASE_FONT_SIZE}
            textRendering={textRendering}
            textAnchor="middle"
            dominantBaseline={textBaseline}
            lengthAdjust="spacing"
            className={hiddenChipTextClassName}
          >
            {displayValue || value}
          </text>
          <text
            fill="#000"
            x="50%"
            y="50%"
            fontSize={fontSize}
            textRendering={textRendering}
            textAnchor="middle"
            dominantBaseline={textBaseline}
            alignmentBaseline={textBaseline}
            lengthAdjust="spacing"
            data-e2e={dataE2e}
            transform={textTransform}
            transform-origin="center"
          >
            {displayValue || value}
          </text>
        </>
      )}
      {hasDropShadow && (
        <defs>
          <filter id="drop-shadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1" />
            <feOffset dx="0" dy=".8" result="offsetblur" />
            <feFlood floodColor="rgba(0,0,0,0.7)" />
            <feComposite in2="offsetblur" operator="in" />
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      )}
    </Container>
  );
};

export default Chip;
